import { useEffect } from 'react';
import { connect } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';

/* Styled Components */
import {
  Container,
  Wrapper,
  Section,
  ErrorMessage,
} from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import SignupHeader from 'components/signup/multiscreen/SignupHeader';
import CreateAccountPage from 'components/signup/multiscreen/CreateAccountPage';
import StoreCategoryPage from 'components/signup/multiscreen/StoreCategoryPage';
import StoreHoursPage from 'components/signup/multiscreen/StoreHoursPage';
import StoreTypePage from 'components/signup/multiscreen/StoreTypePage';
import StoreAddressPage from 'components/signup/multiscreen/StoreAddressPage';
import StoreNamePage from 'components/signup/multiscreen/StoreNamePage';
import SuccessPage from 'components/signup/multiscreen/SuccessPage';
import UnsupportedStoreTypePage from 'components/signup/multiscreen/UnsupportedStoreTypePage';
import WishDotLoaderModal from 'components/common/DotLoaderModal';

/* Services */
import AnalyticsService from 'services/AnalyticsService';

/* Actions */
import { setCountryCode as setCountryCodeAction } from 'app/signupSlice';

/* Selectors */
import {
  getCurrentSignupStep,
  getErrorMessage,
  getIsFormSubmitting,
} from 'app/signupSlice';
import { getCountryCode as getIPCountryCode } from 'app/appSlice';

/* Constants */
import { SIGNUP_SCREEN, SIGNUP_SCREEN_ORDER } from 'constants/SignupConstants';
import { BLUE_VISITOR_UUID } from 'constants/AppConstants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_EVENT_TYPE,
} from 'constants/AnalyticsConstants';

/* Utils */
import { getSessionStorage } from 'utils/SessionStorage';

/* Types */
import type { AppState, AppDispatch } from 'app/store';

const mapStateToProps = (state: AppState) => ({
  currentSignupStep: getCurrentSignupStep(state),
  errorMessage: getErrorMessage(state),
  isSubmitting: getIsFormSubmitting(state),
  ipCountryCode: getIPCountryCode(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setCountryCode: (country: string) => dispatch(setCountryCodeAction(country)),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const BlueMultiScreenSignupPage = ({
  errorMessage,
  currentSignupStep,
  isSubmitting,
  ipCountryCode,
  setCountryCode,
}: PropsType) => {
  useEffect(() => {
    setCountryCode(ipCountryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    AnalyticsService({
      event_type: ANALYTICS_EVENT_TYPE.WISH_LOCAL_WEB_EVENTS,
      event: ANALYTICS_EVENTS.SIGNUP_PAGE_VISIT,
      visitor_uuid: getSessionStorage(BLUE_VISITOR_UUID) || '',
      page_version: 'microservice',
      signup_step: currentSignupStep,
    });
  }, [currentSignupStep]);

  const getStepsCompleted = () => {
    if (currentSignupStep === SIGNUP_SCREEN.UNSUPPORTED_STORE_TYPE) {
      return undefined;
    }
    return SIGNUP_SCREEN_ORDER[currentSignupStep];
  };

  return (
    <>
      <Wrapper>
        <SignupHeader
          stepsCompleted={getStepsCompleted()}
          totalSteps={Object.keys(SIGNUP_SCREEN_ORDER).length - 1}
          showProgressBar={
            !(
              currentSignupStep === SIGNUP_SCREEN.CREATE_ACCOUNT ||
              currentSignupStep === SIGNUP_SCREEN.SUCCESS ||
              currentSignupStep === SIGNUP_SCREEN.UNSUPPORTED_STORE_TYPE
            )
          }
          shouldOpenNewTab
        />
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        <Container size="small" stretch>
          <Section
            noBottomBorder
            noBottomPadding
            shouldRenderMobile={isMobileOnly}
          >
            {currentSignupStep === SIGNUP_SCREEN.CREATE_ACCOUNT && (
              <CreateAccountPage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.STORE_TYPE && (
              <StoreTypePage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.STORE_NAME && (
              <StoreNamePage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.STORE_ADDRESS && (
              <StoreAddressPage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.STORE_HOURS && (
              <StoreHoursPage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.STORE_CATEGORY && (
              <StoreCategoryPage />
            )}
            {currentSignupStep === SIGNUP_SCREEN.SUCCESS && <SuccessPage />}
            {currentSignupStep === SIGNUP_SCREEN.UNSUPPORTED_STORE_TYPE && (
              <UnsupportedStoreTypePage />
            )}
          </Section>
        </Container>
      </Wrapper>
      {isSubmitting && <WishDotLoaderModal />}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BlueMultiScreenSignupPage);
